import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useMemo, useState} from 'react';

import {assert} from 'Asserts';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import {FONT_PROXIMA_12, FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import SearchInput from 'ui-library/SearchInput';
import SelectTree from 'ui-library/select-tree/SelectTree';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';

import {getMethodId, sortComparePMEIs} from './UnitOpLinkingFlowchartUtils';
import {UnitOperationFields} from './useUnitOpLinkingState';

export interface StudyProcedureRunFlowchartSelectTreeProps {
  studyType: StudyType;
  allPMEIs: Array<UnitOperationFields>;
  selectedIds: Array<string>;
  setSelectedIds: (ids: Array<string>) => void;
  setHoveringItemId: (itemId: string | null) => void;
}

export default function StudyProcedureRunFlowchartSelectTree({
  studyType,
  allPMEIs,
  selectedIds,
  setSelectedIds,
  setHoveringItemId,
}: StudyProcedureRunFlowchartSelectTreeProps): JSX.Element {
  return allPMEIs.length > 0 ? (
    <StudyProcedureRunFlowchartSelectTreePresentational
      studyType={studyType}
      allPMEIs={allPMEIs}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      setHoveringItemId={setHoveringItemId}
    />
  ) : (
    <StudyProcedureRunFlowchartSelectTreeEmptyView studyType={studyType} />
  );
}

function StudyProcedureRunFlowchartSelectTreeEmptyView({studyType}: {studyType: StudyType}): JSX.Element {
  return (
    <div className={css(styles.emptyStateContainer)}>
      <div className={css(styles.emptyStateText)}>
        All {getProcedureRunUIStringForStudyType(studyType, {plural: true})} will be listed here.
      </div>
    </div>
  );
}

function StudyProcedureRunFlowchartSelectTreePresentational({
  allPMEIs,
  selectedIds,
  setSelectedIds,
  setHoveringItemId,
}: StudyProcedureRunFlowchartSelectTreeProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const {procedureRunIdToKey, methodIdToKey} = useMemo(() => {
    const procedureRunIdToKey: Record<string, number> = {};
    const methodIdToKey: Record<string, number> = {};
    for (const pmei of allPMEIs) {
      procedureRunIdToKey[pmei.procedureRunId] = pmei.procedureRunOrderKey;
      methodIdToKey[getMethodId(pmei.procedureRunId, pmei.templateMethodId)] = pmei.methodOrderKey;
    }
    return {procedureRunIdToKey, methodIdToKey};
  }, [allPMEIs]);

  const pmeiIdToUnitOperationFields = useMemo(() => {
    const pmeiIdToUnitOperationFields: Record<string, UnitOperationFields> = {};
    for (const pmei of allPMEIs) {
      pmeiIdToUnitOperationFields[pmei.pmeiId] = pmei;
    }
    return pmeiIdToUnitOperationFields;
  }, [allPMEIs]);

  const rootItemSortFn = useCallback(
    (rootItems: Array<TreeItem>) =>
      rootItems.sort((a, b) => procedureRunIdToKey[a.id] - procedureRunIdToKey[b.id]),
    [procedureRunIdToKey]
  );

  const itemSortFn = useCallback(
    (items: Array<TreeItem>) =>
      items.sort((a, b) => {
        // Items can be either a PMEI or a method group.
        const aPMEI = pmeiIdToUnitOperationFields[a.id];
        const bPMEI = pmeiIdToUnitOperationFields[b.id];
        const aMethodKey = methodIdToKey[a.id];
        const bMethodKey = methodIdToKey[b.id];

        if (aPMEI && bPMEI) {
          return sortComparePMEIs(aPMEI, bPMEI);
        } else if (aMethodKey !== undefined && bMethodKey !== undefined) {
          return aMethodKey - bMethodKey;
        } else {
          assert(false, `Items must be either a PMEI or a method group: ${a.id} ${b.id}`);
          return 0;
        }
      }),
    [pmeiIdToUnitOperationFields, methodIdToKey]
  );

  const items = useMemo(() => {
    const treeItems: Array<TreeItem> = [];
    const seenProcedureRunIds = new Set<string>();
    const seenMethodIds = new Set<string>();
    for (const pmei of allPMEIs) {
      assert(pmei.procedureRunName != null, 'Procedure run name is required');
      const methodNameId = getMethodId(pmei.procedureRunId, pmei.templateMethodId);
      // Push the procedure run as a root item
      if (!seenProcedureRunIds.has(pmei.procedureRunId)) {
        treeItems.push({
          id: pmei.procedureRunId,
          name: pmei.procedureRunName,
          parentId: null,
          isInitiallyExpanded: true,
          secondaryLabels: [<span>{pmei.procedureRunDisplayId}</span>],
        });
        seenProcedureRunIds.add(pmei.procedureRunId);
      }
      // Push the method name as a child of the procedure run
      if (!seenMethodIds.has(methodNameId)) {
        treeItems.push({
          id: methodNameId,
          name: pmei.methodName,
          parentId: pmei.procedureRunId,
          isInitiallyExpanded: true,
        });
        seenMethodIds.add(methodNameId);
      }
      // Push the pmei as a child of the method name
      treeItems.push({
        id: pmei.pmeiId,
        name: `${pmei.conditionName} - ${pmei.replicateNumber}`,
        parentId: methodNameId,
        isInitiallyExpanded: true,
      });
    }
    return treeItems;
  }, [allPMEIs]);

  const onHoverItem = useCallback(
    (id: string, isHovering: boolean) => {
      setHoveringItemId(isHovering ? id : null);
    },
    [setHoveringItemId]
  );

  return (
    <>
      <div className={css(styles.searchInputWrapper)}>
        <SearchInput query={searchQuery} doSearch={setSearchQuery} placeholder="Search" />
      </div>
      <SelectTree
        items={items}
        itemTypeName="Unit Operation"
        multiple={false}
        selectAllText="Select All"
        selectedIds={selectedIds}
        onChangeSelectedIds={setSelectedIds}
        rootItemSortFn={rootItemSortFn}
        itemSortFn={itemSortFn}
        className={classNames(css(styles.selectTree), 'selectTree--borderless')}
        itemClassName={css(styles.selectTreeItem)}
        selectedItemClassName={css(styles.selectedTreeItem)}
        onHoverItem={onHoverItem}
        query={searchQuery ?? undefined}
      />
    </>
  );
}

export const TEST_EXPORTS = {
  StudyProcedureRunFlowchartSelectTreePresentational,
  StudyProcedureRunFlowchartSelectTreeEmptyView,
};

const styles = StyleSheet.create({
  selectTree: {...FONT_PROXIMA_12, height: '100%', maxHeight: '100%'},
  selectTreeItem: {
    margin: '0px 8px',
    borderRadius: '4px',
    color: Color.GRAY_900,
    ':hover': {backgroundColor: Color.GRAY_100},
    ':active': {backgroundColor: Color.GRAY_200},
  },
  selectedTreeItem: {
    backgroundColor: Color.BLUE_100,
    color: Color.BLUE_500,
    ':hover': {backgroundColor: Color.BLUE_100},
  },
  searchInputWrapper: {margin: '8px 8px 4px 8px'},
  searchInput: {border: `1px solid ${Color.GRAY_300}`, borderRadius: '4px'},
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    alignItems: 'center',
    ':before': {
      content: "''",
      flex: '0 0 50%',
    },
  },
  emptyStateText: {
    ...FONT_PROXIMA_14,
    color: Color.GRAY_900,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    textAlign: 'center',
  },
});
